import { DeleteOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Space, Spin, Upload } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import React, { useEffect, useState } from 'react';
import { uploadIcon } from '@/services/common';
import { resolveBackendResourceUrl } from '@/utils/backendResource';

interface IconUploaderProps {
  value?: string;
  onChange?: (url: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * 图标上传组件
 * 支持预览、上传、删除功能
 * 用于行业、商区等模块的图标管理
 */
const IconUploader: React.FC<IconUploaderProps> = ({
  value,
  onChange,
  placeholder = '选择图标',
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const previewUrl = resolveBackendResourceUrl(value);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [previewUrl]);

  // 文件验证
  const beforeUpload = (file: RcFile): boolean => {
    const isImage = ['image/png', 'image/jpeg', 'image/jpg'].includes(
      file.type,
    );
    if (!isImage) {
      console.error('只能上传 PNG 或 JPG 格式的图片');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      console.error('文件大小不能超过 5MB');
      return false;
    }

    return true;
  };

  // 处理上传
  const handleUpload = async (info: any) => {
    const file = info.file as UploadFile;
    if (file.status === 'uploading') {
      setLoading(true);
    } else if (file.status === 'done') {
      setLoading(false);
      const response = file.response;
      if (response?.code === 200 && response?.data?.url) {
        onChange?.(response.data.url);
      } else {
        console.error('上传失败');
      }
    } else if (file.status === 'error') {
      setLoading(false);
      console.error('上传出错');
    }
  };

  const handleCustomRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    try {
      const response = await uploadIcon(file as File);
      if (response?.code === 200) {
        onSuccess?.(response, file);
      } else {
        onError?.(new Error(response?.message || '上传失败'));
      }
    } catch (error: any) {
      onError?.(error);
    }
  };

  // 删除图标
  const handleDelete = () => {
    setPreviewError(false);
    onChange?.('');
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #f0f0f0',
              background: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {previewUrl && !previewError ? (
              <img
                src={previewUrl}
                alt="uploaded preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                onError={() => setPreviewError(true)}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  color: '#999',
                  fontSize: 12,
                  textAlign: 'center',
                  padding: '0 8px',
                }}
              >
                <PictureOutlined />
                <span>预览失败</span>
              </div>
            )}
          </div>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            disabled={disabled}
          >
            删除
          </Button>
        </div>
      )}

      <Spin spinning={loading}>
        <Upload
          name="file"
          customRequest={handleCustomRequest}
          beforeUpload={beforeUpload}
          onChange={handleUpload}
          showUploadList={false}
          maxCount={1}
          accept="image/png,image/jpeg,image/jpg"
          disabled={disabled || loading}
        >
          <Button icon={<PlusOutlined />} disabled={disabled || loading}>
            {value ? '更换' : placeholder}
          </Button>
        </Upload>
      </Spin>
    </Space>
  );
};

export default IconUploader;
